import { SnackbarKey, useSnackbar } from 'notistack';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Toast, ToastProps } from 'src/components/Toast';
import { useEnableNewSidebar } from 'src/hooks/useEnableNewSidebar';
import { useAppSelector } from 'src/hooks/useStore';
import { useTrackAppVersion } from 'src/hooks/useTrackAppVersion';
import { RootState } from 'src/store';
import { clearAlertMessage } from 'src/store/ui/actions';
import { AlertVariant } from 'src/store/ui/types';

export const ProgressToast = (
  props: Omit<ToastProps, 'variant' | 'progressValue'>,
) => {
  const progressValue = useAppSelector((state) => state.ui.progressValue);
  return <Toast variant="progress" progressValue={progressValue} {...props} />;
};

const Notifier = () => {
  const dispatch = useDispatch();
  const alertMessage = useSelector((state: RootState) => state.ui.alertMessage);
  const alertMessageType = useSelector(
    (state: RootState) => state.ui.alertMessageType,
  );
  const isProgressComplete = useSelector(
    (state: RootState) => state.ui.isProgressComplete,
  );
  const actionProps = useSelector((state: RootState) => state.ui.actionProps);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const enableNewSidebar = useEnableNewSidebar();
  const { hasNewVersion } = useTrackAppVersion();

  const newVersionSnackbarContent = React.useCallback(
    (key: string) => (
      <div key={key}>
        <Toast
          variant={AlertVariant.WARNING}
          description="A new update is ready"
          actionProps={{
            onClick: () => {
              window.location.reload();
            },
            children: 'Refresh page to update',
            className: 'text-base',
          }}
        />
      </div>
    ),
    [],
  );

  // Show version update snackbar only when the new sidebar is disabled,
  // since the new sidebar handles this via SidebarUpdateCard
  React.useEffect(() => {
    if (hasNewVersion && !enableNewSidebar) {
      enqueueSnackbar('New version available', {
        persist: true,
        key: 'new-version-available',
        preventDuplicate: true,
        content: newVersionSnackbarContent,
      });
    }
  }, [
    hasNewVersion,
    enableNewSidebar,
    enqueueSnackbar,
    newVersionSnackbarContent,
  ]);

  /**
   * when snackbar is closed, clear the alert message
   * this will avoid snackbar from showing up again on re-render or useEffect change
   */
  const handleCloseRequest = () => {
    dispatch(clearAlertMessage());
  };

  const snackbarContent = React.useCallback(
    (key: SnackbarKey) =>
      alertMessageType ? (
        <div key={key}>
          <>
            {alertMessageType === AlertVariant.PROGRESS ? (
              <ProgressToast
                description={alertMessage}
                onClose={() => closeSnackbar(key)}
              />
            ) : (
              <Toast
                variant={alertMessageType}
                description={alertMessage}
                onClose={() => closeSnackbar(key)}
                actionProps={actionProps}
              />
            )}
          </>
        </div>
      ) : null,
    [alertMessage, alertMessageType],
  );

  React.useEffect(() => {
    let timer = 0;
    if (isProgressComplete) {
      timer = window.setTimeout(() => {
        dispatch(clearAlertMessage());
        closeSnackbar('uploadProgress');
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [isProgressComplete]);

  React.useEffect(() => {
    if (alertMessageType) {
      const enqueueDate = new Date().getTime();
      // if an error snackbar is about to be queued, close any existing progress snackbar
      if (alertMessageType === AlertVariant.ERROR) {
        closeSnackbar('uploadProgress');
      }
      enqueueSnackbar(alertMessage, {
        persist: alertMessageType === AlertVariant.PROGRESS,
        key:
          alertMessageType === AlertVariant.PROGRESS
            ? 'uploadProgress'
            : `alert-${alertMessageType}-${enqueueDate}`,
        content: snackbarContent,
        onClose: handleCloseRequest,
      });
    }
  }, [alertMessage, alertMessageType, enqueueSnackbar]);

  return null;
};

export default Notifier;
